import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = ['messages', 'replyForm', 'replyContent', 'cannedSelect', 'submitButton']

  static values = {
    ticketId: String
  }

  connect() {
    this.subscribeToChannel()
    this.scrollToBottom()
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  subscribeToChannel() {
    if (!this.ticketIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'SupportTicketChannel', ticket_id: this.ticketIdValue },
      {
        received: (data) => this.handleChannelMessage(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleChannelMessage(data) {
    switch (data.event) {
      case 'new_message':
        this.appendMessage(data.message)
        break
      case 'ticket_closed':
        this.handleTicketClosed()
        break
      case 'ticket_reopened':
        this.handleTicketReopened()
        break
    }
  }

  appendMessage(message) {
    if (!this.hasMessagesTarget) return

    const messageHtml = this.buildMessageHtml(message)
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  buildMessageHtml(message) {
    const isAdmin = message.user?.is_admin
    const bubbleClass = isAdmin ?
      'bg-slate-900 text-white' :
      'bg-white border-2 border-neutral-200'

    return `
      <div class="flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-4">
        <div class="${bubbleClass} rounded-lg px-4 py-3 max-w-[80%]">
          <div class="text-sm font-medium mb-1">
            ${message.user?.name || 'System'}
            ${isAdmin ? '<span class="ml-2 text-xs opacity-75">Admin</span>' : ''}
          </div>
          <div class="whitespace-pre-wrap">${this.escapeHtml(message.content)}</div>
          <div class="text-xs opacity-60 mt-2">${this.formatTime(message.created_at)}</div>
        </div>
      </div>
    `
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  insertCannedResponse() {
    if (!this.hasCannedSelectTarget || !this.hasReplyContentTarget) return

    const selectedOption = this.cannedSelectTarget.selectedOptions[0]
    if (!selectedOption || !selectedOption.value) return

    const content = selectedOption.dataset.content
    if (content) {
      this.replyContentTarget.value = content
      this.replyContentTarget.focus()
    }

    this.cannedSelectTarget.value = ''
  }

  submitReply(event) {
    event.preventDefault()

    if (!this.hasReplyContentTarget) return

    const content = this.replyContentTarget.value.trim()
    if (!content) return

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = 'Sending...'
    }

    const form = this.hasReplyFormTarget ? this.replyFormTarget : event.target
    const formData = new FormData(form)

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.replyContentTarget.value = ''
        } else {
          this.showNotification(data.error || 'Failed to send message', 'error')
        }
      })
      .catch(() => {
        this.showNotification('Failed to send message', 'error')
      })
      .finally(() => {
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = 'Send Reply'
        }
      })
  }

  handleTicketClosed() {
    window.location.reload()
  }

  handleTicketReopened() {
    window.location.reload()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatTime(timestamp) {
    return new Date(timestamp).toLocaleString()
  }

  showNotification(message, type = 'info') {
    const colors = {
      info: 'bg-blue-50 border-blue-500 text-blue-700',
      success: 'bg-green-50 border-green-500 text-green-700',
      error: 'bg-red-50 border-red-500 text-red-700'
    }

    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded border-2 ${colors[type] || colors.info}`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}
